/**
 * Holiday versions of the level themes: the same theme with a gentle tint of its sky,
 * backdrop and earth (spooky for Halloween: darker, towards purple; cold for winter) and the
 * season's art on its backdrop (seasonBackdrop.ts).
 *
 * Only the scenery changes colour. Everything the player reads to play keeps its colours:
 * the edges of the ground and blocks, spikes, pads, orbs, portals, pickups and the finish.
 */
import type { Season } from '../season/season';
import { mix } from './color';
import type { Theme } from './themes';

interface Tint {
  /** Sky gradient (top, middle, horizon): colours to mix towards, and how much. */
  sky: readonly [string, string, string];
  skyAmount: readonly [number, number, number];
  /** Backdrop silhouettes (far, mid, near). */
  silhouette: string;
  silhouetteAmount: number;
  /** The backdrop's lights (stars, windows, fireflies). */
  glow: string;
  glowAmount: number;
  /** Ground, ceiling and block fills (not their edges). */
  earth: string;
  earthAmount: number;
}

const TINTS: Readonly<Record<Exclude<Season, 'none'>, Tint>> = {
  halloween: {
    sky: ['#12061f', '#1d0a2e', '#3d1a4f'],
    skyAmount: [0.38, 0.36, 0.3],
    silhouette: '#0c0612',
    silhouetteAmount: 0.3,
    glow: '#ffcf70',
    glowAmount: 0.35,
    earth: '#160a20',
    earthAmount: 0.2,
  },
  winter: {
    sky: ['#0a1a3a', '#1e3f6e', '#9cc3e6'],
    skyAmount: [0.25, 0.25, 0.3],
    silhouette: '#2a4466',
    silhouetteAmount: 0.18,
    glow: '#ffffff',
    glowAmount: 0.2,
    earth: '#16263f',
    earthAmount: 0.15,
  },
};

const cache = new Map<Theme, Map<Season, Theme>>();

/**
 * The theme as it looks in `season` (the theme itself when there is none). The same object
 * every time: the backdrop caches its layers by theme.
 */
export function seasonTheme(theme: Theme, season: Season): Theme {
  if (season === 'none') return theme;
  let bySeason = cache.get(theme);
  if (!bySeason) {
    bySeason = new Map();
    cache.set(theme, bySeason);
  }
  let tinted = bySeason.get(season);
  if (!tinted) {
    tinted = tint(theme, season, TINTS[season]);
    bySeason.set(season, tinted);
  }
  return tinted;
}

function tint(theme: Theme, season: Exclude<Season, 'none'>, t: Tint): Theme {
  const sky = (i: 0 | 1 | 2) => mix(theme.sky[i], t.sky[i], t.skyAmount[i]);
  const silhouette = (color: string) => mix(color, t.silhouette, t.silhouetteAmount);
  const earth = (color: string) => mix(color, t.earth, t.earthAmount);
  return {
    ...theme,
    name: `${theme.name}~${season}`,
    season,
    sky: [sky(0), sky(1), sky(2)],
    far: silhouette(theme.far),
    mid: silhouette(theme.mid),
    near: silhouette(theme.near),
    glow: mix(theme.glow, t.glow, t.glowAmount),
    ground: earth(theme.ground),
    groundDark: earth(theme.groundDark),
    block: earth(theme.block),
    blockInner: earth(theme.blockInner),
  };
}
